import { createDeliveryTrackingSession } from './delivery-tracking-session.js';
import { createDeliveryTrackingWakeLock } from './delivery-tracking-wake-lock.js';

const riderStateMessages = {
    sharing:
        'Compartiendo ubicación mientras esta página siga abierta y visible.',
    starting: 'Preparando la sesión de ubicación.',
    requesting_permission:
        'Esperando permiso y una ubicación confirmada por el servidor.',
    owned_elsewhere:
        'Otra pestaña está compartiendo esta salida. Puedes tomar el control explícitamente.',
    session_required:
        'Tu sesión necesita autenticación. El envío está detenido.',
    session_unavailable:
        'No se ha podido verificar la sesión. El envío está pausado.',
    session_conflict:
        'La sesión de ubicación ha cambiado. Verifica la salida antes de continuar.',
    stopped_pending_ack:
        'Envío local detenido; esperando confirmación del servidor.',
    stopped: 'Has detenido el envío de ubicación.',
    paused_hidden: 'Ubicación pausada porque la página no está visible.',
    paused_offline: 'Ubicación pausada mientras no haya conexión.',
    permission_denied: 'El navegador ha denegado el permiso de ubicación.',
    position_unavailable:
        'El navegador no puede obtener la ubicación ahora mismo. Se volverá a intentar.',
    position_timeout:
        'La ubicación ha tardado demasiado. Se volverá a intentar.',
    geolocation_unavailable: 'Este navegador no ofrece ubicación compatible.',
    publish_failed:
        'No se ha podido publicar la ubicación. Se volverá a intentar.',
    mfa_required: 'Verifica tu identidad antes de compartir la ubicación.',
    stop_failed: 'No se ha podido confirmar la detención en el servidor.',
    run_unavailable:
        'La salida ha finalizado o ya no está disponible. El envío de ubicación se ha detenido.',
};

function defaultState(code) {
    return {
        code,
        message:
            riderStateMessages[code] ?? 'El estado de ubicación ha cambiado.',
    };
}

export function createRiderTrackingController(dependencies = {}) {
    const documentTarget = dependencies.document ?? document;
    const windowTarget = dependencies.window ?? window;
    const navigatorTarget = dependencies.navigator ?? navigator;
    const fetchImpl = dependencies.fetch ?? fetch;
    const csrfToken = dependencies.csrfToken ?? '';
    const minimumIntervalMs = dependencies.minimumIntervalMs ?? 9000;
    const geolocationRetryMs = dependencies.geolocationRetryMs ?? 1500;
    const now = dependencies.now ?? Date.now;
    const schedule = dependencies.setTimeout ?? setTimeout;
    const cancelSchedule = dependencies.clearTimeout ?? clearTimeout;
    const onState = dependencies.onState ?? (() => {});
    const onFreshness = dependencies.onFreshness ?? (() => {});
    let optedIn = false;
    let currentRun = null;
    let watcherId = null;
    let lastSentAt = null;
    let queuedPosition = null;
    let pendingTimer = null;
    let retryTimer = null;
    let retryAvailable = true;
    let inFlight = null;
    let generation = 0;
    let session = null;
    let sessionRun = null;
    let terminationCode = null;
    let freshnessTimer = null;
    let lifecycleRevision = 0;
    let destroyed = false;
    let lastState = { ...defaultState('stopped'), lastAcceptedAt: null };
    const sessions = new Map();
    const wakeLock = createDeliveryTrackingWakeLock({
        navigator: navigatorTarget,
        document: documentTarget,
        onState: dependencies.onWakeState,
    });

    const emit = (code) => {
        if (destroyed) return;
        if (freshnessTimer !== null) cancelSchedule(freshnessTimer);
        freshnessTimer = null;
        const lastAcceptedAt = session?.snapshot().lastAcceptedAt ?? null;
        const changed = lastState.code !== code;
        lastState = {
            ...defaultState(code),
            lastAcceptedAt,
        };
        if (changed) onState(lastState);
        else onFreshness(lastState);
        if (optedIn && lastAcceptedAt !== null) {
            freshnessTimer = schedule(() => {
                freshnessTimer = null;
                emit(code);
            }, 1000);
        }
    };
    const sessionFor = (run, activate = true) => {
        if (sessionRun?.runId === run.runId) return session;
        const ownedSession =
            sessions.get(run.runId) ??
            createDeliveryTrackingSession({
                fetch: fetchImpl,
                csrfToken,
                publisherIdFactory: dependencies.publisherIdFactory,
                sessionUrl: run.sessionUrl ?? `${run.stopUrl}/session`,
                stopUrl: run.stopUrl,
                onState: (value) => {
                    if (
                        sessionRun?.runId !== run.runId ||
                        terminationCode !== null
                    )
                        return;
                    if (
                        !['enabled', 'paused', 'stopped'].includes(value.state)
                    ) {
                        if (
                            [
                                'owned_elsewhere',
                                'mfa_required',
                                'session_required',
                                'session_unavailable',
                                'session_conflict',
                            ].includes(value.state) &&
                            !session?.publicationIdentity() &&
                            (optedIn || watcherId !== null || inFlight !== null)
                        ) {
                            optedIn = value.optedIn;
                            pause(value.state);
                        } else emit(value.state);
                    }
                },
            });
        sessions.set(run.runId, ownedSession);
        if (activate) {
            sessionRun = run;
            session = ownedSession;
        }
        return ownedSession;
    };
    const presence = async (state) => {
        const identity = session?.publicationIdentity();
        if (identity === null || identity === undefined || sessionRun === null)
            return false;
        try {
            const response = await fetchImpl(
                `${sessionRun.sessionUrl ?? `${sessionRun.stopUrl}/session`}/presence`,
                {
                    method: 'POST',
                    credentials: 'same-origin',
                    cache: 'no-store',
                    headers: {
                        Accept: 'application/json',
                        'Content-Type': 'application/json',
                        'X-CSRF-TOKEN': csrfToken,
                    },
                    body: JSON.stringify({ ...identity, state }),
                },
            );
            return response.ok;
        } catch {
            return false;
        }
    };

    const clearTimer = () => {
        if (pendingTimer !== null) {
            cancelSchedule(pendingTimer);
            pendingTimer = null;
        }
    };

    const clearRetryTimer = () => {
        if (retryTimer !== null) {
            cancelSchedule(retryTimer);
            retryTimer = null;
        }
    };

    const abortPublish = (completingController = null) => {
        generation += 1;
        if (inFlight !== null) {
            inFlight.controller.abort();
            if (inFlight.controller !== completingController) {
                inFlight = null;
            }
        }
    };

    const clearWatcher = () => {
        if (watcherId !== null && navigatorTarget.geolocation) {
            navigatorTarget.geolocation.clearWatch(watcherId);
            watcherId = null;
        }
    };

    const pause = (code) => {
        clearWatcher();
        clearTimer();
        clearRetryTimer();
        queuedPosition = null;
        abortPublish();
        wakeLock.setSharing(false);
        emit(code);
    };

    const stopLocally = (code, completingController = null) => {
        optedIn = false;
        clearWatcher();
        clearTimer();
        clearRetryTimer();
        queuedPosition = null;
        abortPublish(completingController);
        currentRun = null;
        lastSentAt = null;
        retryAvailable = true;
        wakeLock.setSharing(false);
        emit(code);
    };

    const hasDefinitiveTerminalStatus = (response) =>
        [401, 404, 410, 419].includes(response.status);

    const isTerminalResponse = (response, code) =>
        hasDefinitiveTerminalStatus(response) ||
        (response.status === 409 && code !== 'tracking_sample_rejected');

    const scheduleQueuedPosition = () => {
        if (queuedPosition === null || pendingTimer !== null || !optedIn) {
            return;
        }

        const elapsed =
            lastSentAt === null ? minimumIntervalMs : now() - lastSentAt;
        const delay = Math.max(0, minimumIntervalMs - elapsed);
        pendingTimer = schedule(() => {
            pendingTimer = null;
            const position = queuedPosition;
            queuedPosition = null;
            if (position !== null) {
                publish(position);
            }
        }, delay);
    };

    const publish = (position) => {
        if (
            !optedIn ||
            currentRun === null ||
            documentTarget.hidden ||
            !navigatorTarget.onLine
        ) {
            return;
        }

        if (inFlight !== null) {
            queuedPosition = position;
            return;
        }

        const elapsed =
            lastSentAt === null ? minimumIntervalMs : now() - lastSentAt;
        if (elapsed < minimumIntervalMs) {
            queuedPosition = position;
            scheduleQueuedPosition();
            return;
        }

        const identity = session?.publicationIdentity();
        if (!identity) return;
        lastSentAt = now();
        const requestGeneration = generation;
        const controller = new AbortController();
        inFlight = { controller, generation: requestGeneration };

        Promise.resolve(
            fetchImpl(currentRun.publishUrl, {
                method: 'POST',
                credentials: 'same-origin',
                headers: {
                    Accept: 'application/json',
                    'Content-Type': 'application/json',
                    'X-CSRF-TOKEN': csrfToken,
                },
                body: JSON.stringify({ ...position, ...identity }),
                signal: controller.signal,
            }),
        )
            .then(async (response) => {
                if (
                    requestGeneration !== generation ||
                    controller.signal.aborted
                ) {
                    return;
                }

                if (!response.ok) {
                    if (response.status === 403) {
                        if (inFlight?.controller === controller)
                            inFlight = null;
                        stopLocally('session_required', controller);
                        const authorizationGeneration = generation;
                        const authorizationLifecycle = lifecycleRevision;
                        let code = null;
                        try {
                            code = (await response.json()).code ?? null;
                        } catch {
                            code = null;
                        }
                        if (
                            authorizationGeneration === generation &&
                            authorizationLifecycle === lifecycleRevision &&
                            code === 'mfa_required'
                        )
                            emit('mfa_required');
                        return;
                    }
                    if (hasDefinitiveTerminalStatus(response)) {
                        stopLocally(
                            [401, 419].includes(response.status)
                                ? 'session_required'
                                : 'run_unavailable',
                            controller,
                        );
                        return;
                    }

                    let code = null;
                    try {
                        code = (await response.json()).code ?? null;
                    } catch {
                        code = null;
                    }

                    if (
                        requestGeneration !== generation ||
                        controller.signal.aborted
                    ) {
                        return;
                    }

                    if (isTerminalResponse(response, code)) {
                        stopLocally(
                            code === 'mfa_required'
                                ? 'mfa_required'
                                : 'run_unavailable',
                            controller,
                        );
                    } else {
                        emit('publish_failed');
                    }
                    return;
                }

                const acknowledgment = await response.json();
                if (
                    requestGeneration !== generation ||
                    controller.signal.aborted
                )
                    return;
                if (
                    response.status === 202 &&
                    session.acceptPublication(acknowledgment, identity)
                ) {
                    emit('sharing');
                } else {
                    emit('publish_failed');
                }
            })
            .catch((error) => {
                if (
                    error?.name !== 'AbortError' &&
                    requestGeneration === generation
                ) {
                    emit('publish_failed');
                }
            })
            .finally(() => {
                if (inFlight?.controller === controller) {
                    inFlight = null;
                }
                if (
                    requestGeneration === generation &&
                    !controller.signal.aborted
                ) {
                    scheduleQueuedPosition();
                }
            });
    };

    const handlePosition = (position) => {
        const latitude = Number(position?.coords?.latitude);
        const longitude = Number(position?.coords?.longitude);
        const accuracy = Number(position?.coords?.accuracy);
        const recordedAt = Number(position?.timestamp);

        if (
            ![latitude, longitude, accuracy, recordedAt].every(Number.isFinite)
        ) {
            emit('position_unavailable');
            return;
        }

        retryAvailable = true;

        publish({
            latitude,
            longitude,
            accuracy,
            recorded_at: Math.round(recordedAt),
        });
    };

    const scheduleGeolocationRetry = () => {
        if (
            !retryAvailable ||
            retryTimer !== null ||
            !optedIn ||
            currentRun === null ||
            documentTarget.hidden ||
            !navigatorTarget.onLine
        ) {
            return;
        }

        retryAvailable = false;
        retryTimer = schedule(() => {
            retryTimer = null;
            if (
                optedIn &&
                currentRun !== null &&
                !documentTarget.hidden &&
                navigatorTarget.onLine
            ) {
                beginWatcher();
            }
        }, geolocationRetryMs);
    };

    const handleGeolocationError = (error) => {
        const code =
            error?.code === 1
                ? 'permission_denied'
                : error?.code === 2
                  ? 'position_unavailable'
                  : 'position_timeout';

        if (code === 'permission_denied') {
            terminationCode = code;
            stopLocally(code);
            const requestGeneration = generation;
            void session?.stop().then(() => {
                if (requestGeneration === generation && !optedIn) emit(code);
            });
            return;
        }

        clearWatcher();
        emit(code);
        scheduleGeolocationRetry();
    };

    const beginWatcher = () => {
        if (
            !optedIn ||
            watcherId !== null ||
            currentRun === null ||
            !session?.publicationIdentity()
        ) {
            return;
        }
        if (documentTarget.hidden) {
            emit('paused_hidden');
            return;
        }
        if (!navigatorTarget.onLine) {
            emit('paused_offline');
            return;
        }
        if (!navigatorTarget.geolocation?.watchPosition) {
            emit('geolocation_unavailable');
            return;
        }

        watcherId = navigatorTarget.geolocation.watchPosition(
            handlePosition,
            handleGeolocationError,
            {
                enableHighAccuracy: true,
                maximumAge: 5000,
                timeout: 10_000,
            },
        );
        wakeLock.setSharing(true);
        emit(
            session.snapshot().lastAcceptedAt === null
                ? 'requesting_permission'
                : 'sharing',
        );
    };

    const resume = async () => {
        const requestGeneration = generation;
        const currentLifecycle = lifecycleRevision;
        const ownedSession = session;
        if (!optedIn || documentTarget.hidden || !navigatorTarget.onLine)
            return;
        if (!(await ownedSession.verify())) {
            if (
                requestGeneration === generation &&
                currentLifecycle === lifecycleRevision
            )
                pause(ownedSession.snapshot().state);
            return;
        }
        if (
            requestGeneration !== generation ||
            currentLifecycle !== lifecycleRevision ||
            !optedIn ||
            documentTarget.hidden ||
            !navigatorTarget.onLine
        )
            return;
        if (!(await presence('active'))) {
            if (
                requestGeneration === generation &&
                currentLifecycle === lifecycleRevision
            )
                pause('session_unavailable');
            return;
        }
        if (
            requestGeneration === generation &&
            currentLifecycle === lifecycleRevision
        )
            beginWatcher();
    };

    const handleVisibility = () => {
        if (!optedIn) {
            return;
        }
        if (documentTarget.hidden) {
            void presence('paused_hidden');
            pause('paused_hidden');
        } else if (!navigatorTarget.onLine) {
            void presence('paused_offline');
            pause('paused_offline');
        } else {
            void resume();
        }
    };
    const handleOffline = () => {
        if (optedIn) {
            void presence('paused_offline');
            pause('paused_offline');
        }
    };
    const handleOnline = () => {
        if (optedIn && !documentTarget.hidden) {
            void resume();
        }
    };

    documentTarget.addEventListener('visibilitychange', handleVisibility);
    windowTarget.addEventListener('offline', handleOffline);
    windowTarget.addEventListener('online', handleOnline);

    return {
        async start(run, takeover = false) {
            if (destroyed) return;
            if (!run?.runId || !run.publishUrl || !run.stopUrl) {
                emit('position_unavailable');
                return;
            }
            const currentLifecycle = ++lifecycleRevision;
            if (currentRun !== null && currentRun.runId !== run.runId) {
                await this.stop(null, currentLifecycle);
                if (destroyed || currentLifecycle !== lifecycleRevision) return;
            }
            terminationCode = null;
            currentRun = run;
            const ownedSession = sessionFor(run);
            const requestGeneration = generation;
            const acquired = await (takeover
                ? ownedSession.takeover()
                : ownedSession.start());
            if (
                currentLifecycle !== lifecycleRevision ||
                requestGeneration !== generation ||
                currentRun?.runId !== run.runId
            )
                return;
            optedIn = acquired;
            retryAvailable = true;
            if (acquired) beginWatcher();
            else pause(ownedSession.snapshot().state);
        },
        async stop(requestedRun = null, expectedLifecycle = null) {
            const run = requestedRun ?? currentRun ?? sessionRun;
            if (run === null) return;
            const stopsCurrent =
                currentRun === null || run.runId === currentRun.runId;
            const currentLifecycle =
                expectedLifecycle ??
                (stopsCurrent ? ++lifecycleRevision : lifecycleRevision);
            const ownedSession = sessionFor(run, stopsCurrent);
            if (stopsCurrent) stopLocally('stopped_pending_ack');
            const requestGeneration = generation;
            if (
                ownedSession.snapshot().generation === null &&
                ownedSession.snapshot().state === 'stopped'
            ) {
                if (!(await ownedSession.observe())) {
                    if (stopsCurrent && currentLifecycle === lifecycleRevision)
                        emit('stop_failed');
                    return;
                }
                if (stopsCurrent && currentLifecycle !== lifecycleRevision)
                    return;
            }
            const stopped = await ownedSession.stop();
            if (
                stopsCurrent &&
                requestGeneration === generation &&
                currentLifecycle === lifecycleRevision
            )
                emit(terminationCode ?? (stopped ? 'stopped' : 'stop_failed'));
        },
        setWakeEnabled(value) {
            wakeLock.setEnabled(value);
        },
        wakeSnapshot: () => wakeLock.snapshot(),
        stateSnapshot: () => ({ ...lastState }),
        reconcileRuns(runIds) {
            if (currentRun !== null && !new Set(runIds).has(currentRun.runId)) {
                terminationCode = 'run_unavailable';
                stopLocally('run_unavailable');
                const currentLifecycle = ++lifecycleRevision;
                void session?.stop().then(() => {
                    if (currentLifecycle === lifecycleRevision && !optedIn)
                        emit('run_unavailable');
                });
            }
        },
        destroy() {
            destroyed = true;
            lifecycleRevision += 1;
            void session?.stop();
            wakeLock.destroy();
            optedIn = false;
            if (freshnessTimer !== null) cancelSchedule(freshnessTimer);
            freshnessTimer = null;
            clearWatcher();
            clearTimer();
            clearRetryTimer();
            abortPublish();
            queuedPosition = null;
            currentRun = null;
            documentTarget.removeEventListener(
                'visibilitychange',
                handleVisibility,
            );
            windowTarget.removeEventListener('offline', handleOffline);
            windowTarget.removeEventListener('online', handleOnline);
        },
    };
}

let mountedRider = null;
let mountedCustomer = null;

function riderRunFromButton(button) {
    return {
        runId: button.dataset.runId,
        publishUrl: button.dataset.publishUrl,
        stopUrl: button.dataset.stopUrl,
        sessionUrl: button.dataset.sessionUrl,
    };
}

export function mountRiderTracking(root, dependencies = {}) {
    if (mountedRider?.root === root) {
        mountedRider.controller.reconcileRuns(riderRunIds(root));
        mountedRider.repaint();
        return mountedRider.controller;
    }
    mountedRider?.destroy();

    const externalOnState = dependencies.onState;
    const renderFreshness = (state) => {
        const updated = root.querySelector?.('[data-rider-tracking-updated]');
        if (!updated) return;
        updated.dataset.acceptedAt = state.lastAcceptedAt ?? '';
        const text =
            state.lastAcceptedAt === null
                ? ''
                : `Última ubicación confirmada hace ${Math.max(0, Math.floor(((dependencies.now ?? Date.now)() - Date.parse(state.lastAcceptedAt)) / 1000))} segundos.`;
        if (updated.textContent !== text) updated.textContent = text;
    };
    const renderState = (state) => {
        renderFreshness(state);
        const status = root.querySelector?.('[data-rider-tracking-status]');
        if (status) {
            if (status.textContent !== state.message)
                status.textContent = state.message;
            status.dataset.trackingState = state.code;
        }
    };
    const renderWake = (state) => {
        const checkbox = root.querySelector?.(
            '[data-rider-tracking-wake-lock]',
        );
        if (checkbox) {
            checkbox.disabled = !state.supported;
            checkbox.checked = state.enabled;
        }
        const status = root.querySelector?.(
            '[data-rider-tracking-wake-lock-status]',
        );
        if (status) {
            const text =
                {
                    held: 'Pantalla activa mientras compartes.',
                    unsupported:
                        'Este navegador no permite mantener la pantalla activa.',
                    unavailable: 'No se ha podido mantener la pantalla activa.',
                    released: 'El navegador ha liberado la pantalla.',
                }[state.state] ?? '';
            if (status.textContent !== text) status.textContent = text;
        }
    };
    const controller = createRiderTrackingController({
        ...dependencies,
        csrfToken:
            dependencies.csrfToken ??
            document.querySelector('meta[name="csrf-token"]')?.content ??
            '',
        minimumIntervalMs:
            dependencies.minimumIntervalMs ??
            Number(root.dataset?.throttleMs ?? 9000),
        onWakeState: renderWake,
        onFreshness: renderFreshness,
        onState: (state) => {
            renderState(state);
            externalOnState?.(state);
        },
    });
    const repaint = () => {
        renderState(controller.stateSnapshot());
        renderWake(controller.wakeSnapshot());
    };
    const handleClick = (event) => {
        const takeoverButton = event.target?.closest?.(
            '[data-rider-tracking-takeover]',
        );
        if (takeoverButton && root.contains(takeoverButton)) {
            void controller.start(riderRunFromButton(takeoverButton), true);
            return;
        }
        const startButton = event.target?.closest?.(
            '[data-rider-tracking-start]',
        );
        if (startButton && root.contains(startButton)) {
            controller.start(riderRunFromButton(startButton));
            return;
        }
        const stopButton = event.target?.closest?.(
            '[data-rider-tracking-stop]',
        );
        if (stopButton && root.contains(stopButton)) {
            controller.stop(riderRunFromButton(stopButton));
        }
    };
    const handleChange = (event) => {
        if (event.target?.closest?.('[data-rider-tracking-wake-lock]'))
            controller.setWakeEnabled(event.target.checked);
    };
    const checkbox = root.querySelector?.('[data-rider-tracking-wake-lock]');
    if (checkbox) checkbox.disabled = !controller.wakeSnapshot().supported;
    root.addEventListener('change', handleChange);
    root.addEventListener('click', handleClick);
    const destroy = () => {
        root.removeEventListener('change', handleChange);
        root.removeEventListener('click', handleClick);
        controller.destroy();
    };
    mountedRider = { root, controller, destroy, repaint };

    return controller;
}

function riderRunIds(root) {
    return Array.from(
        root.querySelectorAll?.(
            '[data-rider-tracking-start], [data-rider-tracking-stop]',
        ) ?? [],
        (button) => button.dataset.runId,
    ).filter((runId) => typeof runId === 'string' && runId !== '');
}

export function createCustomerTrackingPoller(dependencies) {
    const documentTarget = dependencies.document ?? document;
    const windowTarget = dependencies.window ?? window;
    const navigatorTarget = dependencies.navigator ?? navigator;
    const fetchImpl = dependencies.fetch ?? fetch;
    const schedule = dependencies.setTimeout ?? setTimeout;
    const cancelSchedule = dependencies.clearTimeout ?? clearTimeout;
    const onState = dependencies.onState ?? (() => {});
    let started = false;
    let timer = null;
    let inFlight = null;

    const clearTimer = () => {
        if (timer !== null) {
            cancelSchedule(timer);
            timer = null;
        }
    };
    const scheduleNext = () => {
        clearTimer();
        if (started && !documentTarget.hidden && navigatorTarget.onLine) {
            timer = schedule(refresh, dependencies.intervalMs ?? 10_000);
        }
    };
    const pause = () => {
        clearTimer();
        inFlight?.abort();
        inFlight = null;
    };
    const refresh = () => {
        if (
            !started ||
            documentTarget.hidden ||
            !navigatorTarget.onLine ||
            inFlight !== null
        ) {
            return;
        }

        const controller = new AbortController();
        inFlight = controller;
        Promise.resolve(
            fetchImpl(dependencies.url, {
                method: 'GET',
                credentials: 'same-origin',
                cache: 'no-store',
                headers: { Accept: 'application/json' },
                signal: controller.signal,
            }),
        )
            .then(async (response) => {
                if (controller.signal.aborted) {
                    return;
                }
                if (!response.ok) {
                    onState({
                        state: 'unavailable',
                        message:
                            'La ubicación del Rider no está disponible ahora mismo.',
                        updated_seconds_ago: null,
                        map_url: null,
                    });
                    return;
                }
                onState(await response.json());
            })
            .catch((error) => {
                if (error?.name !== 'AbortError') {
                    onState({
                        state: 'unavailable',
                        message:
                            'La ubicación del Rider no está disponible ahora mismo.',
                        updated_seconds_ago: null,
                        map_url: null,
                    });
                }
            })
            .finally(() => {
                if (inFlight === controller) {
                    inFlight = null;
                }
                scheduleNext();
            });
    };
    const handleVisibility = () => {
        if (documentTarget.hidden) {
            pause();
        } else if (started && navigatorTarget.onLine) {
            refresh();
        }
    };
    const handleOffline = () => pause();
    const handleOnline = () => {
        if (started && !documentTarget.hidden) {
            refresh();
        }
    };

    return {
        start() {
            if (started) {
                return;
            }
            started = true;
            documentTarget.addEventListener(
                'visibilitychange',
                handleVisibility,
            );
            windowTarget.addEventListener('offline', handleOffline);
            windowTarget.addEventListener('online', handleOnline);
            refresh();
        },
        refresh,
        destroy() {
            started = false;
            pause();
            documentTarget.removeEventListener(
                'visibilitychange',
                handleVisibility,
            );
            windowTarget.removeEventListener('offline', handleOffline);
            windowTarget.removeEventListener('online', handleOnline);
        },
    };
}

function mountCustomerTracking(root) {
    if (mountedCustomer?.root === root) {
        return;
    }
    mountedCustomer?.poller.destroy();

    const status = root.querySelector('[data-customer-tracking-status]');
    const updated = root.querySelector('[data-customer-tracking-updated]');
    const image = root.querySelector('[data-customer-tracking-map]');
    let announcedState = null;
    let announcedMessage = null;
    const poller = createCustomerTrackingPoller({
        url: root.dataset.trackingUrl,
        intervalMs: Number(root.dataset.pollMs ?? 10_000),
        onState: (state) => {
            if (
                status &&
                (state.state !== announcedState ||
                    state.message !== announcedMessage)
            ) {
                status.textContent = state.message;
                status.dataset.trackingState = state.state;
                announcedState = state.state;
                announcedMessage = state.message;
            }
            if (updated) {
                updated.textContent = Number.isInteger(
                    state.updated_seconds_ago,
                )
                    ? `Última actualización: hace ${state.updated_seconds_ago} segundos.`
                    : 'Sin una actualización reciente.';
            }
            if (image) {
                if (typeof state.map_url === 'string' && state.map_url !== '') {
                    if (image.src !== state.map_url) {
                        image.src = state.map_url;
                    }
                    image.hidden = false;
                } else {
                    image.hidden = true;
                    image.removeAttribute('src');
                }
            }
        },
    });
    mountedCustomer = { root, poller };
    poller.start();
}

export function mountDeliveryTracking() {
    const riderRoot = document.querySelector('[data-rider-tracking-root]');
    if (riderRoot) {
        mountRiderTracking(riderRoot);
    } else if (mountedRider !== null) {
        mountedRider.destroy();
        mountedRider = null;
    }

    const customerRoot = document.querySelector(
        '[data-customer-tracking-root]',
    );
    if (customerRoot) {
        mountCustomerTracking(customerRoot);
    } else if (mountedCustomer !== null) {
        mountedCustomer.poller.destroy();
        mountedCustomer = null;
    }
}

export function resetDeliveryTrackingMountsForTests() {
    mountedRider?.destroy();
    mountedCustomer?.poller.destroy();
    mountedRider = null;
    mountedCustomer = null;
}
